import type * as React from 'react'

import { cn } from '../lib/cn'
import { IconCheck, IconWarning } from '../lib/icons'
import { Badge } from './badge'
import { Spinner } from './spinner'

/* A state said in words (family 8), with colour kept for what asks for a hand: `failed` is a
 * red badge, `warning` amber words for a problem to fix before anything else can go on, and
 * every other state grey words. The rule lives here so no caller re-derives it. */

export type StatusTextTone = 'neutral' | 'todo' | 'running' | 'done' | 'failed' | 'warning'

export interface StatusTextProps {
  readonly tone?: StatusTextTone
  readonly children: React.ReactNode
  /** For a control that points at the words with `aria-describedby`. */
  readonly id?: string
  readonly className?: string
}

const TONES: Record<Exclude<StatusTextTone, 'failed'>, string> = {
  neutral: 'text-muted-foreground',
  todo: 'text-muted-foreground/70',
  running: 'text-muted-foreground',
  done: 'text-muted-foreground',
  warning: 'text-warning-text',
}

export function StatusText({ tone = 'neutral', children, id, className }: StatusTextProps) {
  if (tone === 'failed') {
    return (
      <Badge id={id} variant="danger" className={className}>
        {children}
      </Badge>
    )
  }
  // The mark stays on the first line when the words wrap: `mt-0.5` centres it on that line.
  return (
    <span
      id={id}
      className={cn('inline-flex items-start gap-1.5 text-note', TONES[tone], className)}
    >
      <StatusMark tone={tone} />
      {children}
    </span>
  )
}

// Decorative: the words beside it carry the state.
function StatusMark({ tone }: { readonly tone: StatusTextTone }) {
  switch (tone) {
    case 'running':
      return <Spinner className="mt-0.5" label="" size="sm" />
    case 'done':
      return <IconCheck aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
    case 'warning':
      return <IconWarning aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
    default:
      return null
  }
}
